import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import FilterList from "@mui/icons-material/FilterList";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  BoxProps,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Popover,
  Stack,
  Tab,
  TabProps,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import React, { useEffect, useState } from "react";
import { useMenu } from "../../hook/menu";
import {
  useAddNotebookPage,
  useGetNotebookPage,
  useNotebookPagePath,
  usePageNameEditor,
  useSelectedNotebookPage,
  useSetNoteName,
} from "../../hook/page";
import { useFolderPropertyTypes, useFolderSecIndexes } from "../../hook/page/property";
import {
  useDatabaseViews,
  useSelectedDatabaseView,
  useSelectedDatabaseViewId,
  useSwapDatabaseViews,
} from "../../hook/page/view";
import { useViewSetting, useSetViewFilter, useSetViewSort, useSetViewCardProps } from "../../hook/page/viewSetting";
import { PageViewType } from "../../types/notebook/view";
import { DynamicTextField } from "../dynamicTextField";
import { FloatingButton } from "../floatingButton";
import { AddPageMenu } from "../menu/page";
import {
  AddDatabaseViewMenu,
  DatabaseViewMenu,
  NotebookMoreMenu,
} from "../menu/page/view";
import { ModalWindow } from "../modal";
import { BoardView } from "./view/board";
import { DatabaseListView } from "./view/list";
import { DatabaseTableView } from "./view/table";

export const AddPageModal: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
  folderId: string;
}> = ({ open, setOpen, folderId }) => {
  const onClose = () => {
    setOpen(false);
  };
  const [name, setName] = useState<string>("");
  const addNotebookPage = useAddNotebookPage();

  return (
    <ModalWindow open={open}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Add Page</Typography>
        <IconButton onClick={onClose} color="primary">
          <CancelOutlined />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          gap: 2,
        }}
      >
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Name"
        ></TextField>
        <Button
          onClick={() => {
            addNotebookPage({ folderId, name });
            onClose();
          }}
        >
          Save
        </Button>
      </Box>
    </ModalWindow>
  );
};

export const NotebookPageNoteTextFieldElement: React.FC<
  {
    pageId: string;
  } & BoxProps
> = ({ pageId, ...boxProps }) => {
  const { setPageName, pageName } = usePageNameEditor(pageId);
  return (
    <DynamicTextField text={pageName} setText={setPageName} {...boxProps} />
  );
};

export const NotebookPageNoteElement: React.FC<
  {
    pageId: string;
  } & BoxProps
> = ({ pageId, ...boxProps }) => {
  const notebookPagePath = useNotebookPagePath();
  const link = notebookPagePath({ pageId });

  return (
    <FloatingButton
      icon={
        <Link href={link}>
          <OpenInNew />
        </Link>
      }
    >
      <NotebookPageNoteTextFieldElement pageId={pageId} {...boxProps} />
    </FloatingButton>
  );
};

export const NotebookDatabaseToolBar: React.FC<{}> = () => {
  const page = useSelectedNotebookPage();
  const folderId = page!["page-id"];

  const { folderSecIndexes: secIndexes } = useFolderSecIndexes(folderId);
  const [selectedViewId] = useSelectedDatabaseViewId();
  const setting = useViewSetting(selectedViewId);
  const setViewSort = useSetViewSort();
  const setViewFilter = useSetViewFilter();
  const setViewCardProps = useSetViewCardProps();
  const { folderProps } = useFolderPropertyTypes(folderId);

  const activeSort = setting?.sort;
  const activeFilter = setting?.filter;
  const cardProps = setting?.["card-props"] ?? [];

  const toggleCardProp = (name: string) => {
    const next = cardProps.includes(name)
      ? cardProps.filter((p) => p !== name)
      : [...cardProps, name];
    void setViewCardProps(next);
  };

  const {
    anchorEl: moreMenuAnchor,
    open: moreMenuOpen,
    handleOpen: handleMoreMenuOpen,
    onClose: onMoreMenuClose,
  } = useMenu();
  const {
    anchorEl: addPageAnchorEl,
    open: addPageOpen,
    handleOpen: handleAddPageOnOpen,
    onClose: handleAddPageOnClose,
  } = useMenu();
  const {
    anchorEl: settingsAnchorEl,
    open: settingsMenuOpen,
    handleOpen: handleSettingsOpen,
    onClose: handleSettingsClose,
  } = useMenu();

  const [sortProp, setSortProp] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filterProp, setFilterProp] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");

  const onOpenSettings = (e: React.MouseEvent<HTMLElement>) => {
    setSortProp(activeSort?.["prop-name"] ?? "");
    setSortDir(activeSort?.dir ?? "asc");
    setFilterProp(activeFilter?.["prop-name"] ?? "");
    setFilterValue(activeFilter?.value ?? "");
    handleSettingsOpen(e);
  };

  const onApplySort = async () => {
    if (!sortProp) { await setViewSort(undefined); handleSettingsClose(); return; }
    await setViewSort({ "prop-name": sortProp, dir: sortDir });
    handleSettingsClose();
  };

  const onApplyFilter = async () => {
    if (!filterProp) return;
    await setViewFilter({ "prop-name": filterProp, value: filterValue });
    handleSettingsClose();
  };

  return (
    <>
      <AddPageMenu
        anchorEl={addPageAnchorEl}
        folderId={folderId}
        onClose={handleAddPageOnClose}
        open={addPageOpen}
      />
      <NotebookMoreMenu
        anchorEl={moreMenuAnchor}
        open={moreMenuOpen}
        onClose={onMoreMenuClose}
      />
      <Popover
        open={settingsMenuOpen}
        anchorEl={settingsAnchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        elevation={3}
      >
        <Stack sx={{ p: 2, minWidth: 288, gap: 1.5 }}>
          <Typography variant="overline" sx={{ lineHeight: 1, mb: 0.5 }}>Sort</Typography>
          <Select
            value={sortProp}
            onChange={(e) => setSortProp(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {(secIndexes ?? []).map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <ToggleButtonGroup
              value={sortDir}
              exclusive
              onChange={(_, val) => { if (val) setSortDir(val); }}
              size="small"
              disabled={!sortProp}
            >
              <ToggleButton value="asc" aria-label="ascending">
                <ArrowUpward fontSize="small" sx={{ mr: 0.5 }} />Asc
              </ToggleButton>
              <ToggleButton value="desc" aria-label="descending">
                <ArrowDownward fontSize="small" sx={{ mr: 0.5 }} />Desc
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" variant="contained" disableElevation onClick={onApplySort}>
              Apply
            </Button>
          </Box>

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="overline" sx={{ lineHeight: 1, mb: 0.5 }}>Filter</Typography>
          <Select
            value={filterProp}
            onChange={(e) => setFilterProp(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="" disabled><em>Select property</em></MenuItem>
            {(secIndexes ?? []).map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            label="Value"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void onApplyFilter(); }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button size="small" onClick={() => { void setViewFilter(undefined); handleSettingsClose(); }}>Clear</Button>
            <Button size="small" variant="contained" disableElevation onClick={onApplyFilter}>Apply</Button>
          </Box>

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="overline" sx={{ lineHeight: 1, mb: 0.5 }}>Properties</Typography>
          {Object.values(folderProps ?? {}).map((prop) => (
            <FormControlLabel
              key={prop.name}
              control={
                <Checkbox
                  size="small"
                  checked={cardProps.includes(prop.name)}
                  onChange={() => toggleCardProp(prop.name)}
                  data-testid={`card-prop-toggle-${prop.name}`}
                />
              }
              label={prop.name}
            />
          ))}
        </Stack>
      </Popover>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
        {activeSort && (
          <Chip
            size="small"
            icon={activeSort.dir === "asc" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
            label={activeSort["prop-name"]}
            onDelete={() => void setViewSort(undefined)}
          />
        )}
        {activeFilter && (
          <Chip
            size="small"
            label={`${activeFilter["prop-name"]} = ${activeFilter.value}`}
            onDelete={() => void setViewFilter(undefined)}
          />
        )}
      </Box>
      <IconButton color="primary" onClick={onOpenSettings}>
        <FilterList />
      </IconButton>
      <Divider orientation="vertical" flexItem />
      <IconButton color="primary" onClick={handleAddPageOnOpen}>
        <Add />
      </IconButton>
      <IconButton onClick={handleMoreMenuOpen} color="primary">
        <MoreVert />
      </IconButton>
    </>
  );
};

export const DatabaseViewTab: React.FC<{ index: number } & TabProps> = ({
  index: i,
  ...tabProps
}) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: i,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    cursor: "grab",
  };
  return (
    <Tab
      style={style}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      {...tabProps}
    />
  );
};

export const DatabaseViewsTabs = React.memo(() => {
  const page = useSelectedNotebookPage();
  const folderId = page!["page-id"];
  const pageViews = useDatabaseViews(folderId);
  const [selectedDatabaseView, setSelectedDatabaseView] =
    useSelectedDatabaseViewId();
  const swapDatabaseViews = useSwapDatabaseViews();
  const [selectedViewMenuId, setSelectedViewMenuId] = useState<
    string | undefined
  >();
  const hasTabs = selectedDatabaseView && pageViews.length !== 0;
  const {
    open: viewMenuOpen,
    anchorEl: viewMenuAnchorEl,
    onClose: handleViewMenuClose,
    handleOpen: handleViewMenuOpen,
    setAnchorEl: setViewMenuAnchorEl,
  } = useMenu();
  const {
    open: addViewMenuOpen,
    anchorEl: addViewMenuAnchorEl,
    onClose: handleAddViewMenuClose,
    handleOpen: handleAddViewMenuOpen,
  } = useMenu();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
  );

  useEffect(() => {
    if (pageViews.length === 0) return;
    if (selectedDatabaseView !== undefined) return;
    setSelectedDatabaseView(pageViews[0]["view-id"]);
  }, [pageViews, folderId]);

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over) return;
    const active = event.active.id as number;
    const over = event.over!.id as number;
    const activeView = pageViews[active];
    const overView = pageViews[over];
    swapDatabaseViews({ active: activeView, over: overView });
  };

  return (
    <>
      <AddDatabaseViewMenu
        folderId={folderId}
        onClose={handleAddViewMenuClose}
        anchorEl={addViewMenuAnchorEl}
        open={addViewMenuOpen}
      />
      <DatabaseViewMenu
        folderId={folderId}
        onClose={handleViewMenuClose}
        anchorEl={viewMenuAnchorEl}
        open={viewMenuOpen}
        viewId={selectedViewMenuId}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={pageViews.map((view, i) => i)}
          strategy={horizontalListSortingStrategy}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {hasTabs && (
              <Tabs
                value={selectedDatabaseView}
                onChange={(e, value) => {
                  setSelectedDatabaseView(value);
                }}
              >
                {pageViews.map((pageView, i) => {
                  const name = pageView.name;
                  return (
                    <DatabaseViewTab
                      index={i}
                      // ref={(r) => {
                      //   if (i !== 0) return;
                      //   if (!r) return;
                      //   setViewMenuAnchorEl(r);
                      //   setSelectedViewMenuId(pageView["view-id"]);
                      // }}
                      onContextMenu={(e) => {
                        handleViewMenuOpen(e);
                        setSelectedViewMenuId(pageView["view-id"]);
                      }}
                      key={i}
                      label={name}
                      value={pageView["view-id"]}
                    />
                  );
                })}
              </Tabs>
            )}
            <IconButton
              color="primary"
              ref={(e) => {
                // if (anchorEl) return;
                // setAnchorEl(e);
              }}
              onClick={handleAddViewMenuOpen}
            >
              <Add />
            </IconButton>
          </Box>
        </SortableContext>
      </DndContext>
    </>
  );
});

export const DatabaseView = React.memo(() => {
  const selectedDatabaseView = useSelectedDatabaseView();
  if (!selectedDatabaseView) return <></>;
  const viewType = selectedDatabaseView["view-type"];

  switch (viewType) {
    case PageViewType.BOARD:
      return <BoardView />;
    // case PageViewType.LIST:
    //   return <DatabaseListView />;
    case PageViewType.TABLE:
      return <DatabaseTableView />;
  }
});

export const DatabaseViews = React.memo(() => {
  return (
    <>
      <DatabaseViewsTabs />
      <DatabaseView />
    </>
  );
});
